import React from 'react';
import { Link } from 'react-router-dom';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import { N800 } from '@atlaskit/theme/colors';
import { CommerceAddress, Maybe, TasstaxIdLabel, TaxId as TaxIdType } from '@css-xp/graphtypes/src/generated-types-new';
import { SelfAssessMessage, VATSTATUS } from '@atlassian/cssxp-types-billing';
import { ENABLE_MULTIPLE_TAX_IDS } from '@atlassian/cssxp-utils-generic';
import { OpenSidebarButton, SummaryGridItem } from '../../application';
import { useFeatureToggle } from '../../hooks';
import { TaxId } from '../../tax-id';
import './tax-component.scss';
import { TaxExempt } from './tax-exempt';

export interface TaxComponentProps {
  billToAddress: CommerceAddress | undefined | null;
  taxExemptFlag?: string;
  transactionAccountNumber: string | null | undefined;
  labelTaxId?: string | null;
  labelTaxExempt?: string | null;
  indicator?: string | null;
  quotePage?: boolean;
  shouldRenderTaxExempt?: boolean;
  gridItemClassName?: string;
}

const TAX_EXEMPTION_AUDIT_LOGS = 'taxExemptionAuditLogs';

export const TaxComponent = ({
  billToAddress,
  taxExemptFlag,
  transactionAccountNumber,
  quotePage,
  shouldRenderTaxExempt = true,
  gridItemClassName = '',
}: TaxComponentProps) => {
  const [isEnabled] = useFeatureToggle();
  const isAlternateTaxationEnabled = isEnabled(ENABLE_MULTIPLE_TAX_IDS);
  const renderTaxExemption = (taxIdLabel?: string | null, taxExempt?: string | null) =>
    shouldRenderTaxExempt && (
      <>
        <TaxExempt taxExempt={isAlternateTaxationEnabled ? taxExempt : taxExemptFlag} label={taxIdLabel}>
          <>
            <OpenSidebarButton
              sidebar={TAX_EXEMPTION_AUDIT_LOGS}
              payload={{ transactionAccountNumber }}
              appearance="link"
              sidebarOptions={{
                sidebarWidth: '600px',
                isOverlay: true,
              }}
              testId="tax-exemption-audit-logs"
              spacing="none"
              iconBefore={<WatchIcon label="View history" size="small" primaryColor={N800} />}
              className="view-history-button"
              label="View history"
            >
              <span className="view-history-button-text">View history</span>
            </OpenSidebarButton>
            <br />
            <Link
              to={{ pathname: `https://app.certcapture.com/` }}
              onClick={(e) => e.stopPropagation()}
              target={'_blank'}
              className="cert-capture-link"
              style={{ fontWeight: 'bold' }}
              data-testid="click"
            >
              {'Go to CertCapture'}
            </Link>
          </>
        </TaxExempt>
      </>
    );

  const renderTaxIdAndTaxExemptionColumn = (vatStatus?: string | null, taxExempt?: string | null) => {
    return (
      <>
        {!quotePage ? (
          <>
            <SummaryGridItem className={`section-item-internal ${gridItemClassName}`}>
              <TaxId taxId={billToAddress?.taxId} vatStatus={vatStatus} />
            </SummaryGridItem>
            <SummaryGridItem className={`section-item-internal ${gridItemClassName}`}>
              {renderTaxExemption(null, taxExempt)}
            </SummaryGridItem>
          </>
        ) : (
          <>
            <TaxId taxId={billToAddress?.taxId} vatStatus={vatStatus} />
            <span>{renderTaxExemption()}</span>
          </>
        )}
      </>
    );
  };

  if (isAlternateTaxationEnabled) {
    const taxLabels = billToAddress?.taxLabels;
    let taxIdLabels: TasstaxIdLabel[] | undefined;
    if (taxLabels && taxLabels.length > 0) {
      taxIdLabels = taxLabels[0]?.taxIdLabels as TasstaxIdLabel[];
    }

    if (billToAddress?.taxIds && billToAddress?.taxIds?.length > 0) {
      const billToTaxIds = [...billToAddress.taxIds];
      billToTaxIds.sort((a: Maybe<TaxIdType>, b: Maybe<TaxIdType>) => {
        const labelA = a?.label || '';
        const labelB = b?.label || '';
        if (labelA < labelB) {
          return -1;
        }
        if (labelA > labelB) {
          return 1;
        }
        return 0;
      });
      let vatStatus = null;
      //Unsure of impact of this but this is stopping rendering multiple taxation in chile. i.e. unable to render tax id
      // if (isCountryChile) {
      //   vatStatus = billToAddress.taxIds.find((taxId) => taxId?.label === TAX_LABEL.PST)?.metadata?.vatStatus;
      //   return renderTaxIdAndTaxExemptionColumn(vatStatus);
      // }
      return (
        <>
          {billToTaxIds.map((taxId) => {
            vatStatus = taxId?.metadata?.vatStatus ?? null;
            const taxIdLabel = taxIdLabels?.filter(({ type }) => type && type === taxId?.label) ?? [];
            const displayTaxLabel = taxIdLabel.length > 0 ? taxIdLabel[0].displayName : '';
            const taxExempt = taxId?.taxExempt ? 'Yes' : 'No';
            return (
              <>
                {!quotePage ? (
                  <div key={taxId?.id}>
                    <SummaryGridItem className={`section-item-internal ${gridItemClassName}`}>
                      <div>
                        <label>{`${displayTaxLabel} Tax ID`}</label>
                        <div data-testid="tax-id">{taxId?.id}</div>
                        {vatStatus
                          ? vatStatus === VATSTATUS.self_assess_and_pay
                            ? SelfAssessMessage.REQUIRED
                            : SelfAssessMessage.NOT_REQUIRED
                          : null}
                      </div>
                      <div className="tax-exempt-section">{renderTaxExemption(displayTaxLabel, taxExempt)}</div>
                    </SummaryGridItem>
                  </div>
                ) : (
                  <div key={taxId?.id}>
                    <div>
                      <label>{`${displayTaxLabel ? displayTaxLabel : ''} Tax ID`}</label>
                      <div data-testid="tax-id">{taxId?.id}</div>
                      {vatStatus
                        ? vatStatus === VATSTATUS.self_assess_and_pay
                          ? SelfAssessMessage.REQUIRED
                          : SelfAssessMessage.NOT_REQUIRED
                        : null}
                    </div>
                    <div style={{ marginLeft: '20px' }}>{renderTaxExemption(displayTaxLabel, taxExempt)}</div>
                  </div>
                )}
              </>
            );
          })}
        </>
      );
    }

    return renderTaxIdAndTaxExemptionColumn(null, taxExemptFlag);
  }

  return renderTaxIdAndTaxExemptionColumn();
};
